import React, { useState, useEffect } from 'react';
import { Calendar, PieChart, FilePlus, AlertTriangle, Flag, Plus, Pencil, Trash2, Check, X, GripVertical } from 'lucide-react';
import useDragReorder from '../../hooks/useDragReorder';
import { sans, mono, green, greenLight, greenDark, coral, coralSoft, amber, red, redLight, useTheme } from '../../tokens';
import { getAlerts, saveAlerts, subscribeToAlertsChanges } from '../../data/alertsStore';

// INV-18 -- Recent updates/alerts timeline feed (editable)

const DOT_COLORS = {
  milestone: green,
  board: amber,
  cap_table: '#3B82F6',
  document: '#9E9E9E',
  warning: red,
};

const ICON_MAP = {
  milestone: Flag,
  board: Calendar,
  cap_table: PieChart,
  document: FilePlus,
  warning: AlertTriangle,
};

const TYPE_LABELS = {
  milestone: 'Milestone',
  board: 'Board',
  cap_table: 'Cap Table',
  document: 'Document',
  warning: 'Alert',
};

const ALERTS_FALLBACK = [
  { id: 1, type: 'milestone', date: '2026-03-15', title: 'EFS Georgia: Patient #3 enrolled successfully' },
  { id: 2, type: 'board', date: '2026-03-10', title: 'Board meeting minutes distributed -- Q1 review approved' },
  { id: 3, type: 'cap_table', date: '2026-03-05', title: 'Employee option grants processed (Q1 2026 batch)' },
  { id: 4, type: 'document', date: '2026-02-28', title: 'Monthly investor report published -- January 2026' },
  { id: 5, type: 'warning', date: '2026-02-20', title: 'SPCC ownership approaching warning threshold (58.7%)' },
  { id: 6, type: 'milestone', date: '2026-02-15', title: 'FDA Pre-Submission feedback received -- favorable' },
  { id: 7, type: 'document', date: '2026-02-10', title: 'Updated financial model shared with lead investors' },
  { id: 8, type: 'board', date: '2026-02-01', title: 'Compensation committee approved 2026 ESOP allocation' },
];

function relativeTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

// Filter alerts that belong to a given period (month)
function alertBelongsToPeriod(alert, period) {
  if (!period || !alert.date) return true;
  // period format: 'jan-2026' -> match alerts in that month
  const [mon, yr] = period.split('-');
  const monthMap = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const mm = monthMap[mon] || '01';
  const prefix = `${yr}-${mm}`;
  return alert.date.startsWith(prefix);
}

export default function AlertsFeed({ readOnly = false, selectedPeriod, companyId }) {
  const t = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editType, setEditType] = useState('milestone');
  const [editDate, setEditDate] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('milestone');
  const [newDate, setNewDate] = useState(new Date().toISOString().split('T')[0]);

  // Persistent per-company alerts. Subscribe so completion alerts pushed
  // by ProductRoadmap show up without a page reload.
  const [allAlerts, setAllAlerts] = useState(() => {
    const stored = getAlerts(companyId);
    return stored.length ? stored : ALERTS_FALLBACK.map(a => ({ ...a }));
  });
  useEffect(() => subscribeToAlertsChanges(() => setAllAlerts(getAlerts(companyId))), [companyId]);

  // Filter to current period only
  const alerts = selectedPeriod
    ? allAlerts.filter(a => alertBelongsToPeriod(a, selectedPeriod))
    : allAlerts;

  // Wrap setters so they persist through the store
  const setAlerts = (fn) => {
    setAllAlerts(prev => {
      const next = typeof fn === 'function' ? fn(prev) : fn;
      saveAlerts(companyId, next);
      return next;
    });
  };

  const visible = expanded ? alerts : alerts.slice(0, 5);

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditTitle(a.title);
    setEditType(a.type);
    setEditDate(a.date);
  };

  const saveEdit = () => {
    setAlerts(prev => prev.map(a =>
      a.id === editingId ? { ...a, title: editTitle, type: editType, date: editDate } : a
    ));
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const deleteAlert = (id) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  };
  const drag = useDragReorder(allAlerts, (next) => setAlerts(next));

  const addAlert = () => {
    if (!newTitle.trim()) return;
    const newId = Date.now();
    setAlerts(prev => [{ id: newId, type: newType, date: newDate, title: newTitle.trim() }, ...prev]);
    setNewTitle('');
    setNewType('milestone');
    setNewDate(new Date().toISOString().split('T')[0]);
    setShowAddForm(false);
  };

  const inputStyle = {
    fontFamily: sans, fontSize: 11, padding: '4px 8px',
    border: `1px solid ${t.border}`, borderRadius: 4,
    background: t.bg, color: t.textPrimary, outline: 'none',
  };

  const selectStyle = {
    ...inputStyle, fontSize: 10, padding: '3px 6px',
  };

  return (
    <div>
      {/* Add button */}
      {!readOnly && !showAddForm && (
        <div style={{ marginBottom: 12 }}>
          <button onClick={() => setShowAddForm(true)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: coralSoft, border: `1px solid ${coral}`, borderRadius: 6,
            padding: '5px 12px', cursor: 'pointer', color: coral,
            fontFamily: sans, fontSize: 10, fontWeight: 600,
          }}>
            <Plus size={12} /> Add Update
          </button>
        </div>
      )}

      {/* Add form */}
      {!readOnly && showAddForm && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 8,
          background: t.bgSurface, border: `1px solid ${t.border}`,
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select value={newType} onChange={e => setNewType(e.target.value)} style={selectStyle}>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
              style={{ ...inputStyle, fontSize: 10, width: 120 }} />
          </div>
          <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Update title..."
            style={{ ...inputStyle, width: '100%', marginBottom: 8, boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={addAlert} disabled={!newTitle.trim()} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: newTitle.trim() ? coral : t.borderLight,
              border: 'none', borderRadius: 4, padding: '4px 10px',
              color: newTitle.trim() ? '#fff' : t.textMuted,
              fontFamily: sans, fontSize: 10, fontWeight: 600, cursor: newTitle.trim() ? 'pointer' : 'not-allowed',
            }}>
              <Check size={11} /> Save
            </button>
            <button onClick={() => { setShowAddForm(false); setNewTitle(''); }} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: `1px solid ${t.border}`, borderRadius: 4,
              padding: '4px 10px', color: t.textSecondary,
              fontFamily: sans, fontSize: 10, cursor: 'pointer',
            }}>
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {alerts.length === 0 && (
        <div style={{ padding: '16px 0', textAlign: 'center', fontSize: 11, fontFamily: sans, color: t.textFaint }}>
          No updates for this period.
        </div>
      )}

      {/* Timeline layout */}
      <div style={{ paddingLeft: 14 }}>
        {visible.map((a, i) => {
          const Icon = ICON_MAP[a.type] || FilePlus;
          const dotColor = DOT_COLORS[a.type] || t.textMuted;
          const isEditing = editingId === a.id;
          const dateObj = new Date(a.date);
          const formattedDate = !isNaN(dateObj) ? dateObj.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : a.date;

          const rowDrag = !readOnly && !isEditing ? drag.rowProps(a.id) : {};
          const isDragTarget = drag.dragOverId === a.id;
          return (
            <div key={a.id}
              {...rowDrag}
              style={{
                ...(rowDrag.style || {}),
                display: 'flex', gap: 10, marginBottom: 0,
                position: 'relative',
                padding: '8px 6px',
                background: isDragTarget ? greenLight : 'transparent',
                borderRadius: 4,
                transition: 'background 0.15s',
              }}>
              {/* Vertical line connector */}
              {i < visible.length - 1 && (
                <div style={{
                  position: 'absolute', left: 10, top: 30, bottom: 0,
                  width: 1.5, background: t.borderLight,
                }} />
              )}

              {/* Icon circle */}
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: dotColor + '12',
                border: `1.5px solid ${dotColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, zIndex: 1,
              }}>
                <Icon size={11} color={dotColor} />
              </div>

              {/* Content */}
              <div style={{ flex: 1, paddingTop: 1 }}>
                {isEditing ? (
                  <div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                      <select value={editType} onChange={e => setEditType(e.target.value)} style={selectStyle}>
                        {Object.entries(TYPE_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                      <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
                        style={{ ...inputStyle, fontSize: 10, width: 120 }} />
                    </div>
                    <input type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)}
                      style={{ ...inputStyle, width: '100%', marginBottom: 6, boxSizing: 'border-box' }}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    />
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={saveEdit} style={{
                        display: 'flex', alignItems: 'center', gap: 3,
                        background: coral, border: 'none', borderRadius: 3,
                        padding: '3px 8px', color: '#fff',
                        fontFamily: sans, fontSize: 9, fontWeight: 600, cursor: 'pointer',
                      }}>
                        <Check size={10} /> Save
                      </button>
                      <button onClick={cancelEdit} style={{
                        display: 'flex', alignItems: 'center', gap: 3,
                        background: 'none', border: `1px solid ${t.border}`, borderRadius: 3,
                        padding: '3px 8px', color: t.textMuted,
                        fontFamily: sans, fontSize: 9, cursor: 'pointer',
                      }}>
                        <X size={10} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 500, fontFamily: sans,
                        color: t.textPrimary,
                      }}>
                        {a.title}
                      </span>
                    </div>
                    <span style={{
                      fontSize: 9, fontFamily: mono, color: t.textFaint,
                      flexShrink: 0, whiteSpace: 'nowrap',
                    }}>
                      {formattedDate}
                    </span>
                    {!readOnly && (
                    <div style={{ display: 'flex', gap: 2, flexShrink: 0, opacity: 0.5, paddingTop: 2 }}>
                      <span title="Drag to reorder" style={{ display: 'flex', alignItems: 'center', color: t.textFaint, padding: 2, cursor: 'grab' }}>
                        <GripVertical size={12} />
                      </span>
                      <button onClick={() => startEdit(a)} title="Edit" style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: t.textMuted,
                      }}>
                        <Pencil size={12} />
                      </button>
                      <button onClick={() => deleteAlert(a.id)} title="Delete" style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: red,
                      }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {alerts.length > 5 && (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              background: 'none', border: 'none',
              color: coral, fontSize: 10, fontWeight: 600,
              fontFamily: sans, cursor: 'pointer',
              padding: '4px 12px',
            }}
          >
            {expanded ? 'Show less' : `Show all (${alerts.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
